import React, { useState } from 'react';
import {
  FileText, Download, Eye, Trash2, ChevronRight, ExternalLink,
} from 'lucide-react';
import {
  fileListUl, fileListBordered, fileListEmpty, fileRow, fileRowPad,
  fileRowMarked, fileRowMuted, fileRowIcon, fileRowName, fileRowNameLink,
  fileRowBadge, fileRowMeta, fileRowMetaSoft, fileRowMetaLong, fileRowMetaWrap,
  fileRowIconSm, fileRowNameSm, fileRowNameLinkSm, fileRowMetaSm,
  fileRowAlign, fileRowActions, fileAction,
  fileActionDanger, fileActionUndo, fileActionIconBtn, filePrevToggle, filePrevChevron,
} from '../workflow/constants';

// A file that is already stored, printed (docs/workflow-ui.md §4.3). The
// counterpart of FormKit's FilePicker: that one is "choose a file", this one is
// "a file is here". Every list of stored files in the system is built from the
// pieces below — request attachments, engagement documents, course materials —
// so the row height, the name truncation, the badge weight and the wording of
// the links are the same wherever a filename appears.
//
// What differs between lists is data, not layout, and is passed in: which
// badges a file carries, where its download lives, whether it may be removed.
//
// Reviewing an attachment should be a click, not a download. Images and PDFs
// open in a new tab (the server sends those inline — see
// server/utils/fileResponse.js, which also explains why only those two);
// .doc/.docx still download because no browser renders them. The label says
// which of the two will happen, so the button never lies.
export const VIEWABLE = /\.(pdf|png|jpe?g|gif|webp)$/i;

export const isViewable = (name) => VIEWABLE.test(String(name || ''));

/** Opens in a tab or downloads, whichever the file will actually do. */
export const ViewOrDownload = ({ href, name, label = true }) => {
  const viewable = isViewable(name);
  const Icon = viewable ? Eye : Download;
  return (
    <a
      href={href}
      {...(viewable ? { target: '_blank', rel: 'noreferrer' } : {})}
      className={fileAction}
      title={viewable ? 'View' : 'Download'}
    >
      <Icon className="w-4 h-4" />
      {label && (viewable ? 'View' : 'Download')}
    </a>
  );
};

/** Always a download, whatever the file is. */
export const DownloadLink = ({ href, label = 'Download', newTab = false }) => (
  <a
    href={href}
    {...(newTab ? { target: '_blank', rel: 'noreferrer' } : {})}
    className={fileAction}
    title="Download"
  >
    <Download className="w-4 h-4" />
    {label}
  </a>
);

/** Everything in the list as one zip. */
export const DownloadAllLink = ({ href, count }) => (
  <a href={href} className={fileAction}>
    <Download className="w-4 h-4" /> Download All{count != null ? ` (${count})` : ''}
  </a>
);

/**
 * Takes a file off the list. Two shapes, because the two situations are not
 * the same thing:
 *   mode="mark"   the Modify modal — marked now, deleted when the modal is
 *                 SAVED, so a mis-click is undone with Undo and Cancel throws
 *                 the whole thing away.
 *   mode="now"    the file goes immediately (the caller confirms first).
 */
export const RemoveFileButton = ({
  mode = 'now', marked = false, disabled = false, onClick, title = 'Remove',
}) => {
  if (mode === 'mark') {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={marked ? fileActionUndo : fileActionDanger}
      >
        {marked ? 'Undo' : (<><Trash2 className="w-4 h-4" /> Remove</>)}
      </button>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} className={fileActionIconBtn}>
      <Trash2 className="w-4 h-4" />
    </button>
  );
};

/**
 * One file.
 *
 *   name      the filename as uploaded
 *   href      makes the name itself a link (materials do; attachments don't —
 *             they carry the link in `actions` so the label can say View or
 *             Download)
 *   icon      overrides the default document icon (a link row, a video)
 *   badges    [[label, className], …] — doc type, source, "Reference"
 *   meta      nodes printed small and grey after the name (size, request no,
 *             who uploaded it and when)
 *   stacked   meta on a second line under the name instead of beside it. For
 *             rows whose badges and meta are too long to sit on one line —
 *             beside the name they do not wrap, they overflow.
 *   align     the row's vertical alignment; 'items-start' for a list whose
 *             names wrap (a narrow table cell), so the icon stays on the
 *             first line instead of floating to the middle.
 *   dense     one size down, for a list nested inside a compact reading row
 *             whose own text is text-xs.
 *   marked    struck through — marked for removal, not yet removed
 *   muted     faded — a superseded version
 *   actions   the controls on the right
 */
export const FileRow = ({
  name, href, icon: Icon = FileText, external = false,
  badges = [], meta = [], stacked = false, marked = false, muted = false,
  actions = null, className = '', nameClass = 'truncate', longMeta = false,
  pad = fileRowPad, align = fileRowAlign, dense = false,
}) => {
  let metaCls = dense ? fileRowMetaSm : fileRowMeta;
  if (longMeta) metaCls = fileRowMetaLong;
  else if (stacked && !dense) metaCls = fileRowMetaSoft;
  const iconCls = dense ? fileRowIconSm : fileRowIcon;
  const nameCls = dense ? fileRowNameSm : fileRowName;
  const linkCls = dense ? fileRowNameLinkSm : fileRowNameLink;
  const bits = [
    // A badgeFor that returns no label for this file prints no badge at all,
    // rather than an empty pill.
    ...badges.filter((b) => b && b[0]).map(([label, cls], i) => (
      <span key={`b${i}`} className={`${fileRowBadge} ${cls || ''}`}>{label}</span>
    )),
    ...meta.filter((m) => m != null && m !== '').map((m, i) => (
      <span key={`m${i}`} className={metaCls}>{m}</span>
    )),
  ];
  // `truncate` is overflow:hidden, which does NOTHING on a non-replaced inline
  // element — so the name can only be clipped by a box that is block or a flex
  // item. Hence the two shapes below: the linked name lives inside an
  // inline-flex anchor (its children are flex items, and max-w-full keeps the
  // anchor itself inside the row), and the plain name is written straight into
  // the block that carries nameClass rather than wrapped in a span.
  const linkedName = (
    <a href={href} target="_blank" rel="noreferrer" className={`${linkCls} max-w-full inline-flex items-center gap-1`}>
      <span className={nameClass}>{name}</span>
      {external && <ExternalLink className="w-3.5 h-3.5 shrink-0 text-slate-400" />}
    </a>
  );

  return (
    <li className={`${fileRow} ${align} ${pad} ${muted ? fileRowMuted : ''} ${className}`}>
      {Icon && <Icon className={iconCls} />}
      {stacked ? (
        <div className="min-w-0 flex-1">
          <div className={`${nameCls} ${href ? '' : nameClass} ${marked ? fileRowMarked : ''}`}>
            {href ? linkedName : name}
          </div>
          {bits.length > 0 && <div className={fileRowMetaWrap}>{bits}</div>}
        </div>
      ) : (
        <span className={`flex items-center gap-2 flex-1 ${nameCls} ${marked ? fileRowMarked : ''}`}>
          {href ? linkedName : <span className={nameClass}>{name}</span>}
          {bits}
        </span>
      )}
      {actions && <div className={fileRowActions}>{actions}</div>}
    </li>
  );
};

/**
 * The list itself.
 *
 *   files     the rows' data
 *   row       (file, index) => FileRow props
 *   rowKey    (file, index) => key  (defaults to file.id)
 *   emptyText printed instead of the list when there is nothing; omit to
 *             render nothing at all
 *   bordered  boxed, for a list that sits inside a form
 *   divided   dividers between rows; off for the lists whose rows are their
 *             own little boxes
 */
const FileList = ({
  files = [], row, rowKey, emptyText = null, bordered = false, divided = true,
  className = '',
}) => {
  if (!files.length) {
    return emptyText ? <p className={fileListEmpty}>{emptyText}</p> : null;
  }
  return (
    <ul className={`${divided ? fileListUl : ''} ${bordered ? fileListBordered : ''} ${className}`}>
      {files.map((f, i) => (
        <FileRow key={rowKey ? rowKey(f, i) : (f.id ?? i)} {...row(f, i)} />
      ))}
    </ul>
  );
};

/**
 * Superseded versions, collapsed. Kept for audit and never the first thing
 * anyone needs, so the panel opens closed and prints the count — "there are
 * older ones" is the part that has to be visible without a click.
 */
export const PreviousVersions = ({ count, children }) => {
  const [open, setOpen] = useState(false);
  if (!count) return null;
  return (
    <div className="border-t border-slate-100 pt-2 mt-1">
      <button type="button" onClick={() => setOpen((v) => !v)} className={filePrevToggle}>
        <ChevronRight className={`${filePrevChevron} ${open ? 'rotate-90' : ''}`} />
        Previous Versions ({count})
      </button>
      {open && children}
    </div>
  );
};

export default FileList;
